import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { oxceedPreview, getDailyUnits } from '../qlik';
import { BOLAG, RADER, GRUPP_LABELS } from './budgetModel';
import { fmtMonth } from '../fmt';
import { kontoGrupp } from '../sie';

// ── OXCEED (SANDBOX) ──────────────────────────────────────────────
// Bokföringen ur Fortnox via Oxceed, visad innan den släpps in i modellerna.
// Fliken anropar samma hämtare som den schemalagda körningen skulle använda,
// med insamlare i stället för skrivare mot intel_bokforing — det som står här
// är alltså exakt vad som skulle sparats, ner till kronan.
//
// Hämtningen sparas i intel_oxceed_sandbox, en egen collection som ingen
// annan vy läser. Fliken laddar därför direkt och man kan jämföra mellan
// tillfällen. Portalens modeller ligger i intel_bokforing och nås bara av
// den schemalagda prefetchOxceed, som avsiktligt inte är påslagen än.
//
// Tre saker att titta på:
//  · Mot Qlik — säljer vi det bokföringen påstår? Fäll ut en månad så syns
//    det per kostnadsställe, vilket är där en avvikelse går att förklara.
//  · Ofördelat — hur mycket ligger utanför kostnadsställena. Lönerna ligger
//    på gamla personkoder tills revisionsbyrån fördelat om dem.
//  · Statuskolumnen — skulle hämtningen skriva över en befintlig SIE-månad?

const GRUPPER = ['intakt', 'vk', 'ovrigt', 'personal', 'avskr', 'finans'];
// Qlik känner bara de tre grupper som passerar kassa- och lönesystemen.
const QLIK_GRUPPER = ['intakt', 'vk', 'personal'];

// Kostnadsställe → Qlik-resultatenheter. Bokföringens kostnadsställen och
// Qliks resultatenheter är två indelningar av samma verksamhet: de flesta
// paras uppenbart, men 100 Hotell samlar flera Qlik-enheter och Event och
// Stigmyhrs saknar motsvarighet helt. Parningen är ett antagande — sitter den
// fel syns det som en orimlig diff på just den raden, vilket är hela poängen.
const KS_QLIK = {
  100: ['Hotell Logi', 'Hotell Förmedling', 'Hotell Aktivitet', 'Hotellrestaurangen'],
  101: ['Knallen'],
  102: ['Vävstugan'],
  103: ['Stugservice'],
  106: ['Brasserie Kerstin'],
  108: ['Sportcenter'],
  110: ['OH', 'Saknas'],
};

const tkr = (v) => (v ? Math.round(v / 1000).toLocaleString('sv-SE') : '–');
const kr = (v) => Math.round(v || 0).toLocaleString('sv-SE');
const resultatAv = (s) => (s.intakt || 0) - (s.vk || 0) - (s.ovrigt || 0) - (s.personal || 0) - (s.avskr || 0) - (s.finans || 0);
const tomSumma = () => ({ intakt: 0, vk: 0, ovrigt: 0, personal: 0, avskr: 0, finans: 0 });

export default function OxceedTab() {
  const [bolag, setBolag] = useState('hotell');
  const [period, setPeriod] = useState(null);       // { fromYm, tomYm } — null = innevarande
  const [arslista, setArslista] = useState([]);     // räkenskapsår ur Oxceed
  const [data, setData] = useState(null);           // svaret från oxceedPreview
  const [befintligt, setBefintligt] = useState({}); // ym → { summor, kalla } ur intel_bokforing
  const [qlik, setQlik] = useState(null);           // ym → enhet → { intakt, vk, personal }
  const [lage, setLage] = useState('oxceed');       // oxceed | qlik | ofordelat
  const [oppenMan, setOppenMan] = useState(null);
  const [hamtar, setHamtar] = useState(false);
  const [fel, setFel] = useState('');

  // tvinga = hämta nytt från Oxceed (~30 s). Utan den kommer det sparade
  // tillbaka direkt, eller { tom: true } om bolaget aldrig hämtats.
  const hamta = useCallback(async (b, tvinga, per) => {
    setHamtar(true); setFel(''); setData(null); setOppenMan(null);
    try {
      const [svar, snap, units] = await Promise.all([
        oxceedPreview({ bolag: b, tvinga: !!tvinga, ...(per || {}) }),
        getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', b))),
        getDailyUnits().catch(() => []),
      ]);
      if (Array.isArray(svar.rakenskapsar) && svar.rakenskapsar.length) setArslista(svar.rakenskapsar);
      const bef = {};
      snap.forEach((d) => { const v = d.data(); if (v.ym && v.summor) bef[v.ym] = { summor: v.summor, kalla: v.kalla || '' }; });

      // Behåll resultatenheten — utan den går diffen inte att bryta ned.
      const agg = {};
      for (const u of units) {
        const ym = u.datum.slice(0, 4) + u.datum.slice(5, 7);
        const perEnhet = agg[ym] || (agg[ym] = {});
        const m = perEnhet[u.enhet] || (perEnhet[u.enhet] = { intakt: 0, vk: 0, personal: 0 });
        m.intakt += u.intakt || 0;
        m.vk += Math.max(Math.abs(u.kostnad || 0) - Math.abs(u.lon || 0), 0);
        m.personal += Math.abs(u.lon || 0);
      }
      setBefintligt(bef); setQlik(agg); setData(svar);
    } catch (e) {
      setFel(e.message || 'Hämtningen misslyckades');
    } finally { setHamtar(false); }
  }, []);

  // Vid byte av bolag eller år: visa det sparade direkt, hämta inte.
  useEffect(() => { hamta(bolag, false, period); }, [bolag, period, hamta]);

  const bolagsInfo = data?.bolag?.[bolag] || null;
  const dokument = useMemo(() => (data?.dokument || []).slice().sort((a, b) => a.ym.localeCompare(b.ym)), [data]);
  const reskontra = data?.reskontra?.[bolag] || null;

  // Samma enhetsurval som UtfallsMatris använder, så bolagstotalen här och
  // den i utfallsvyn är samma siffra.
  const enhetsUrval = useMemo(() => {
    const s = new Set((RADER[bolag] || []).filter((r) => r.qlikEnhet).map((r) => r.qlikEnhet));
    if (bolag === 'hotell') { s.add('OH'); s.add('Saknas'); s.delete('Sportcenter'); }
    return s;
  }, [bolag]);

  const qlikManad = useCallback((ym) => {
    const per = qlik?.[ym] || {};
    const ut = { intakt: 0, vk: 0, personal: 0 };
    for (const [enhet, v] of Object.entries(per)) {
      if (!enhetsUrval.has(enhet)) continue;
      for (const g of QLIK_GRUPPER) ut[g] += v[g] || 0;
    }
    return ut;
  }, [qlik, enhetsUrval]);

  // Uppfällningen: per kostnadsställe, med Qlik bredvid där en parning finns.
  const ksRader = useMemo(() => {
    const d = dokument.find((x) => x.ym === oppenMan);
    if (!d || !d.harKs) return null;
    const per = {};
    for (const r of d.rader) {
      const g = kontoGrupp(r.konto);
      if (!g) continue;
      const kod = r.ks || '—';
      const rad = per[kod] || (per[kod] = tomSumma());
      rad[g] += g === 'intakt' ? -r.belopp : r.belopp;
    }
    const qPer = qlik?.[oppenMan] || {};
    const anvanda = new Set();
    const rader = Object.entries(per).map(([kod, ox]) => {
      const enheter = KS_QLIK[kod] || null;
      let q = null;
      if (enheter) {
        q = { intakt: 0, vk: 0, personal: 0 };
        for (const e of enheter) {
          anvanda.add(e);
          const v = qPer[e]; if (!v) continue;
          for (const g of QLIK_GRUPPER) q[g] += v[g] || 0;
        }
      }
      return { kod, ox, q, enheter };
    });
    // Qlik-enheter som ingen kostnadsställeparning fångar upp — de skulle
    // annars försvinna tyst och göra bolagsdiffen oförklarlig.
    const oparade = Object.entries(qPer)
      .filter(([e, v]) => enhetsUrval.has(e) && !anvanda.has(e) && (v.intakt || v.vk || v.personal))
      .map(([e, v]) => ({ kod: null, enhetNamn: e, ox: null, q: v }));
    rader.sort((a, b) => (a.kod === '—' ? 1 : b.kod === '—' ? -1 : a.kod.localeCompare(b.kod)));
    return [...rader, ...oparade];
  }, [dokument, oppenMan, qlik, enhetsUrval]);

  const chip = (active) => ({
    background: active ? '#44365C' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });
  const td = { fontSize: 11.5, padding: '3px 8px 3px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };
  const th = { ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' };
  const panel = { border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 14 };
  const dampad = { fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' };
  const diffFarg = (v, referens) => {
    if (!referens) return 'inherit';
    const andel = Math.abs(v) / Math.abs(referens);
    return andel > 0.2 ? '#A32D2D' : andel > 0.05 ? '#A36A2D' : 'inherit';
  };

  return (
    <div style={{ padding: '4px 2px 24px' }}>
      <div style={{ ...panel, background: 'rgba(68,54,92,0.06)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, fontFamily: 'system-ui,sans-serif' }}>Sandbox — rör inte bokföringsmodellen</div>
        <div style={dampad}>
          Samma hämtare som den schemalagda körningen, men den skriver till en egen collection i stället för
          till bokföringen. Siffrorna här är exakt det som skulle hamnat i modellen. Utfallsmatris,
          styrelserapport och likviditet är orörda tills automatiken slås på.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        {BOLAG.map((b) => (
          <button key={b.id} style={chip(bolag === b.id)} disabled={hamtar} onClick={() => setBolag(b.id)}>{b.namn}</button>
        ))}
        <button
          style={{ ...chip(false), background: '#2E6E8E', color: '#fff', borderColor: '#2E6E8E', opacity: hamtar ? 0.6 : 1 }}
          disabled={hamtar}
          onClick={() => hamta(bolag, true, period)}
        >
          {hamtar ? 'Arbetar…' : data?.sparadAt ? 'Hämta på nytt' : 'Hämta'}
        </button>
        {data?.sparadAt && !hamtar && (
          <span style={dampad}>sparad {new Date(data.sparadAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</span>
        )}
        {hamtar && <span style={dampad}>ett helt räkenskapsår tar en dryg minut</span>}
      </div>

      {/* Räkenskapsår ur Oxceed. Fjolåret är det intressanta att jämföra mot:
          då låg lönerna på avdelning, innevarande år ligger de på personkoder. */}
      {arslista.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ ...dampad, marginRight: 2 }}>Räkenskapsår</span>
          {arslista.map((a) => {
            const vald = (period ? period.fromYm === a.fromYm : data?.period?.fromYm === a.fromYm);
            return (
              <button
                key={a.fromYm}
                style={chip(vald)}
                disabled={hamtar}
                onClick={() => setPeriod({ fromYm: a.fromYm, tomYm: a.tomYm })}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      )}

      {fel && <div style={{ ...panel, borderColor: '#A32D2D', color: '#A32D2D', fontSize: 12 }}>{fel}</div>}

      {data?.tom && !hamtar && (
        <div style={dampad}>Det här bolaget är inte hämtat än. Tryck Hämta.</div>
      )}

      {data && !data.tom && (
        <>
          <div style={panel}>
            <div style={{ fontSize: 12, fontFamily: 'system-ui,sans-serif', marginBottom: 6 }}>
              <strong>{bolagsInfo?.foretag}</strong>
              {bolagsInfo?.synk && (
                <span style={dampad}>
                  {'  ·  '}{bolagsInfo.synk.kalla} synkad {new Date(bolagsInfo.synk.senast).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
                  {'  ·  '}{bolagsInfo.synk.status}
                </span>
              )}
            </div>
            <div style={dampad}>
              Kostnadsställen: {bolagsInfo?.rapport
                ? <>ur rapporten <strong>{bolagsInfo.rapport}</strong></>
                : <>ingen rapport med kostnadsställen hittades — siffrorna blir bolagstotaler utan uppdelning</>}
              {'  ·  '}period {data.period?.fromYm}–{data.period?.tomYm}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {[['oxceed', 'Oxceed'], ['qlik', 'Mot Qlik'], ['ofordelat', 'Ofördelat']].map(([id, label]) => (
              <button key={id} style={chip(lage === id)} onClick={() => setLage(id)}>{label}</button>
            ))}
            <span style={{ ...dampad, alignSelf: 'center' }}>
              {lage === 'oxceed' && 'tkr per månad, som det skulle sparats'}
              {lage === 'qlik' && 'Oxceed minus Qlik i tkr — fäll ut en månad för att se var diffen sitter'}
              {lage === 'ofordelat' && 'tkr som ligger utanför kostnadsställena'}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Månad</th>
                  {GRUPPER.map((g) => <th key={g} style={th}>{GRUPP_LABELS[g]}</th>)}
                  <th style={th}>Resultat</th>
                  <th style={{ ...th, textAlign: 'left', paddingLeft: 10 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dokument.map((d) => {
                  const bef = befintligt[d.ym];
                  const q = qlikManad(d.ym);
                  const visa = (g) => {
                    if (lage === 'ofordelat') return d.ofordelat ? tkr(d.ofordelat[g]) : '–';
                    if (lage === 'qlik') return QLIK_GRUPPER.includes(g) ? tkr((d.summor[g] || 0) - (q[g] || 0)) : '·';
                    return tkr(d.summor[g]);
                  };
                  const res = resultatAv(d.summor);
                  const oppen = oppenMan === d.ym;
                  return (
                    <React.Fragment key={d.ym}>
                      <tr
                        style={{ borderTop: '1px solid var(--border)', cursor: d.harKs ? 'pointer' : 'default' }}
                        onClick={() => d.harKs && setOppenMan(oppen ? null : d.ym)}
                      >
                        <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                          {d.harKs && <span style={{ color: 'var(--text-secondary)', marginRight: 4 }}>{oppen ? '▾' : '▸'}</span>}
                          {fmtMonth(d.ym)}
                        </td>
                        {GRUPPER.map((g) => (
                          <td key={g} style={{ ...td, color: lage === 'qlik' && QLIK_GRUPPER.includes(g) ? diffFarg((d.summor[g] || 0) - (q[g] || 0), q[g]) : 'inherit' }}>
                            {visa(g)}
                          </td>
                        ))}
                        <td style={{ ...td, fontWeight: 700, color: lage === 'oxceed' ? (res >= 0 ? '#2E7D32' : '#A32D2D') : 'inherit' }}>
                          {lage === 'oxceed' ? tkr(res) : '·'}
                        </td>
                        <td style={{ ...td, textAlign: 'left', paddingLeft: 10, fontWeight: 400, color: 'var(--text-secondary)' }}>
                          {d.rader.length} rader
                          {!d.harKs && ' · utan KS'}
                          {bef && (bef.kalla === 'oxceed'
                            ? ' · uppdaterar hämtad månad'
                            : ` · skulle skriva över ${bef.kalla || 'befintlig månad'}`)}
                        </td>
                      </tr>

                      {oppen && ksRader && (
                        <tr>
                          <td colSpan={GRUPPER.length + 3} style={{ padding: '4px 0 14px 18px' }}>
                            {lage === 'qlik' ? (
                              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...th, textAlign: 'left' }}>Kostnadsställe</th>
                                    {QLIK_GRUPPER.map((g) => (
                                      <th key={g} style={{ ...th, textAlign: 'right' }} colSpan={3}>{GRUPP_LABELS[g]}</th>
                                    ))}
                                  </tr>
                                  <tr>
                                    <th style={{ ...th, textAlign: 'left' }} />
                                    {QLIK_GRUPPER.map((g) => (
                                      <React.Fragment key={g}>
                                        <th style={{ ...th, fontWeight: 400 }}>Oxceed</th>
                                        <th style={{ ...th, fontWeight: 400 }}>Qlik</th>
                                        <th style={th}>Diff</th>
                                      </React.Fragment>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {ksRader.map((r) => (
                                    <tr key={r.kod ?? `q:${r.enhetNamn}`} style={{ borderTop: '1px solid var(--border)' }}>
                                      <td style={{ ...td, textAlign: 'left' }}>
                                        {r.kod === null
                                          ? <em style={{ color: 'var(--text-secondary)' }}>{r.enhetNamn} — bara i Qlik</em>
                                          : r.kod === '—'
                                            ? <em style={{ color: 'var(--text-secondary)' }}>utan kostnadsställe</em>
                                            : <>{r.kod} {data.meta?.ks?.[r.kod] || ''}</>}
                                      </td>
                                      {QLIK_GRUPPER.map((g) => {
                                        const ox = r.ox ? r.ox[g] : null;
                                        const qv = r.q ? r.q[g] : null;
                                        const diff = ox !== null && qv !== null ? ox - qv : null;
                                        return (
                                          <React.Fragment key={g}>
                                            <td style={td}>{ox === null ? '·' : tkr(ox)}</td>
                                            <td style={{ ...td, color: 'var(--text-secondary)' }}>{qv === null ? '·' : tkr(qv)}</td>
                                            <td style={{ ...td, fontWeight: 600, color: diff === null ? 'inherit' : diffFarg(diff, qv) }}>
                                              {diff === null ? '·' : tkr(diff)}
                                            </td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...th, textAlign: 'left' }}>Kostnadsställe</th>
                                    {GRUPPER.map((g) => <th key={g} style={th}>{GRUPP_LABELS[g]}</th>)}
                                    <th style={th}>Resultat</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ksRader.filter((r) => r.ox).map((r) => (
                                    <tr key={r.kod} style={{ borderTop: '1px solid var(--border)' }}>
                                      <td style={{ ...td, textAlign: 'left' }}>
                                        {r.kod === '—'
                                          ? <em style={{ color: 'var(--text-secondary)' }}>utan kostnadsställe</em>
                                          : <>{r.kod} {data.meta?.ks?.[r.kod] || ''}</>}
                                      </td>
                                      {GRUPPER.map((g) => <td key={g} style={td}>{tkr(r.ox[g])}</td>)}
                                      <td style={{ ...td, fontWeight: 600 }}>{tkr(resultatAv(r.ox))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {lage === 'qlik' && (
                              <div style={{ ...dampad, marginTop: 6 }}>
                                Parningen kostnadsställe → resultatenhet är ett antagande: 100 Hotell jämförs mot
                                Hotell Logi, Förmedling, Aktivitet och Hotellrestaurangen tillsammans, 110 OH mot OH och Saknas.
                                104 Event och 107 Stigmyhrs har ingen Qlik-motsvarighet och visas med punkt.
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!dokument.length && (
                  <tr><td colSpan={GRUPPER.length + 3} style={{ ...td, textAlign: 'left', paddingTop: 8 }}>Inga bokförda månader i perioden.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {reskontra && (
            <div style={{ ...panel, marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'system-ui,sans-serif' }}>
                Leverantörsreskontra — {reskontra.antalObetalda} obetalda av {reskontra.antalTotalt}, {kr(reskontra.summaObetalt)} kr
              </div>
              {reskontra.aldrar && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Ej förfallet', 'Sen 0–30 d', 'Sen 31–90 d', 'Sen 91–365 d', 'Sen >1 år'].map((h) => <th key={h} style={th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={td}>{kr(reskontra.aldrar.framat)}</td>
                        <td style={td}>{kr(reskontra.aldrar.d30)}</td>
                        <td style={td}>{kr(reskontra.aldrar.d90)}</td>
                        <td style={{ ...td, color: reskontra.aldrar.d365 ? '#A36A2D' : 'inherit' }}>{kr(reskontra.aldrar.d365)}</td>
                        <td style={{ ...td, color: reskontra.aldrar.aldre ? '#A32D2D' : 'inherit' }}>{kr(reskontra.aldrar.aldre)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ ...dampad, marginTop: 6 }}>
                Poster som förfallit för länge sedan är oftast omatchade rester, inte skulder. De behöver
                städas i Fortnox innan reskontran duger som underlag för likviditeten.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
